import './HexPatternIntervals.scss'
import { MutableRefObject, useRef, useState } from "react"
import './HexPatternPositions.scss'
import { motion } from "framer-motion"
import { BsXCircle } from "react-icons/bs";
import { BsCheckCircle } from "react-icons/bs";
import { BsQuestionCircle } from "react-icons/bs";
import { produce } from 'immer'
import { NoteLine } from '../../../Comps/Connectors/NoteLine';
import { HxMapGroup } from '../../../Comps/HxMap/HxMapGroup';
import { HexMap } from '../../../Data/HexMap/HexMap';
import { HexMapBuilderParams, BuildHexMap } from '../../../Data/HexMap/HexMapBuilder';
import { HexMapModifier } from '../../../Data/HexMap/HexMapModifier';
import { Note } from '../../../Data/HexMap/Note';
import { ClassNames } from '../../../Util/ClassNames';
import { All } from '../../../Util/Constants/Quantities';
import { TransformTranslate } from '../../../Util/Transform/Transform';
import { PositionSelector } from '../../../Util/PositionSelector/PositionSelector';
import { OutlineBox } from '../../../Comps/OutlineBox/OutlineBox';
import { ExerciseMessage } from '../Utils/Message/ExerciseMessage';
import { useNotePlayer } from '../../../Sound/SoundStore';
import { NotePlayer } from '../../../Sound/NotePlayer';
import { TRUE } from 'sass';
import { Music } from '../../../Util/Music/Music';
export function HexPatternIntervals(){
    const notePlayer:NotePlayer = useNotePlayer()
    const tonic:number = Music.G
    const keyNotes:number[] = Music.getKeyScale(tonic)
    const maxSelectionHistory:number = 4
    const radius:number = 130
    const hexWidth:number = radius * 2.8
    const hexHeight:number = radius * 2.8
    const positionSelector:MutableRefObject<PositionSelector> = useRef(new PositionSelector(maxSelectionHistory))
    const selectedPosition:MutableRefObject<number> = useRef(positionSelector.current.getRandomPosition(-1))
    const isDouble = (position:number) => { return ( ![2,6].includes(position) )}
    const getMessage = () => {
        const firstNoteLabel:string = String(selectedPosition.current + 1)
        const secondNoteLabel:string = String(((selectedPosition.current+1)%7) + 1)
        if(answerIs.current === null){
            return `What is the interval from the ${firstNoteLabel} to ${secondNoteLabel}?`
        }
        if(answerIs.current === true){
            return `Correct!`
        }
        return `Incorrect.`
    }
    const answerIs:MutableRefObject<boolean|null> = useRef(null)
    const showNextPosition = () => {
        answerIs.current = null
        intervalLine.current = getIntervalLine()
        selectedPosition.current = positionSelector.current.getRandomPosition(selectedPosition.current)
        const firstPosition:number = selectedPosition.current
        const secondPosition:number = (firstPosition + 1) % 7
        setMap(produce((draft)=>{
            mod.current.setDraft(draft)
            mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
            mod.current.notes_setSelectedState([firstPosition, secondPosition], Note.SelectedState_Hilited)
        }))
        setMessage(getMessage())
    }
    const onCorrect = () => {
        const firstPosition:number = selectedPosition.current
        const secondPosition:number = (firstPosition + 1) % 7
        notePlayer.playNotes([keyNotes[firstPosition], keyNotes[secondPosition]], true, 800)
        answerIs.current = true
        intervalLine.current = getIntervalLine()
        setMessage(getMessage())
        setTimeout(showNextPosition, 1500)
    }
    const resetIncorrect = () => {
        answerIs.current = null
        setMessage(getMessage())
    }
    const onIncorrect = () => {
        notePlayer.playBuzzer()
        answerIs.current = false
        setMessage(getMessage())
        setTimeout(resetIncorrect, 1500)
    }
    const onAnswer = (intervalNum:number) => {
        const answer:number = (isDouble(selectedPosition.current)) ? 2 : 1
        if(intervalNum === answer){onCorrect()}
        else{ onIncorrect()}
    }
    const getMap = () => {
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:tonic, radius:radius
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        const firstPosition:number = selectedPosition.current
        const secondPosition:number = (firstPosition + 1) % 7
        //mod.notes_setDefined([All], false)
        mod.notes_setPrimaryLabelAsPosition([All])
        mod.notes_setSelectedState([All], Note.SelectedState_Default)
        mod.notes_setSelectedState([firstPosition, secondPosition], Note.SelectedState_Hilited)
        return map
    }
    const [map, setMap] = useState(getMap())
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(map))
    const [message, setMessage] = useState(getMessage())

    const getIntervalLine = () => {
        if(answerIs.current !== true){return null}
        const firstNote:Note = map.notes[selectedPosition.current]
        const secondNote:Note = map.notes[(selectedPosition.current + 1)%7]
        return( <NoteLine startNote={firstNote}
        endNote={secondNote}
        double={isDouble(selectedPosition.current)}
        animate={true} displayConfig={map.params.displayConfig} /> 
        )
    }
    const intervalLine:MutableRefObject<React.ReactNode> = useRef(null)
    const onKeyClick = () => {}
    const onNoteClick = () => {}
    return(
        <OutlineBox title={'The HexPattern Interval Exercise'} width={600}>
            <div className={'hex-pattern'}>
                <ExerciseMessage key={Date.now()} message={message} answerStatus={answerIs.current}/>
                <svg width={hexWidth} height={hexHeight}>
                    <g transform={TransformTranslate(hexWidth/2,hexHeight/2)}>
                        <HxMapGroup hexMap={map} onKeyClick={onKeyClick} onNoteClick={onNoteClick}>
                            {intervalLine.current}
                        </HxMapGroup>
                    </g>
                </svg>
                <div>
                    <button type='button' onClick={(e:React.MouseEvent)=>{onAnswer(1)}}>One Note</button>
                    <button type='button' onClick={(e:React.MouseEvent)=>{onAnswer(2)}}>Two Notes</button>
                </div>
            </div>
        </OutlineBox>
    )
}
function Message(props:{message:string, correct:boolean|null}){
    const scale:number = 0
    const animateScale:number = 1
    const getClassNames = () => {
        const classNames:ClassNames = new ClassNames(['message'])
        if(props.correct !== null){
            const className:string = (props.correct) ? 'correct' : 'incorrect'
            classNames.addClass(className)
        }
        return classNames.toString()
    }
    const getIcon = ():React.ReactNode => {
        if(props.correct !== null){
            const icon:JSX.Element = (props.correct) ? <BsCheckCircle/> : <BsXCircle />
            return icon
        }
        return <BsQuestionCircle/>
    }
    return(
        <div className='centered'>
            <motion.div initial={{scale:scale}} animate={{scale:animateScale}} transition={{duration:0.4}} className={getClassNames()}>
                <div className='container'><span>{props.message}</span>{getIcon()}</div>
            </motion.div>
        </div> 
    )
}